import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import { IDataDetalharPedido } from "../../../../../../../types/api/comercial/detalhar-pedido";
import { TableCellSemBorder } from "../StepExpedicao";
import SelectCulturaReceituario from "../../../../../../../components/SelectsSimpleApiQuery/SelectCulturaReceituario";
import { useMemo, useState } from "react";
import { CulturaPragaReceituario } from "../../../../../../../types/api/comercial/pesquisar-cultura-praga-receituario";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import SelectAgronomo from "../../../../../../../components/SelectsSimpleApiQuery/SelectAgronomo";
import { IDataAgronomo } from "../../../../../../../types/api/geral/pesquisar-agronomo";
import { DadoValorPrinter } from "../../../../../../../components/BasicComponents/DisplayDados/DadoValorPrinter";

export default function DialogGerarReceituario({
    open,
    data,
    handleClose,
}: DialogGerarReceituarioProps
) {

    const queryClient = useQueryClient();
    const itemReceituario = data.itens.filter(item => item.emite_receita === "S")

    const [internForm, setInternForm] = useState<InternForm[] | null>(null)
    const [agronomo, setAgronomo] = useState<IDataAgronomo | null>(data.agronomo)

    //Querys e mutations
    const { mutate: gerarReceituarios, isLoading } = useMutation({
        mutationKey: 'receber-pagamento',
        mutationFn: (input: any) => axiosClient.post(`/comercial/pedidos/gerar-receituario-agronomico`, input),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao gerar receituário!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(`comercial/detalhar-pedido`)
            enqueueSnackbar('Receituário gerado com sucesso!', { variant: 'success' });
            handleClose();
        }
    })
    //

    //Variáveis de controle
    const permiteGerar = useMemo(() => {
        return itemReceituario.every(i => i.emite_receita === "S" && (i.alerta === null && i.agrotoxico_id !== null) && agronomo)
    }, [itemReceituario, agronomo]);

    const alerta = useMemo(() => {
        return itemReceituario.filter(i => i.alerta)
    }, [itemReceituario]);

    const culturasPreenchidas = useMemo(() => {
        if (internForm) {
            return internForm.filter(i => i.cultura_id && i.praga_id).length === itemReceituario.length
        }
        return false
    }, [internForm, itemReceituario])


    //

    // Funções manipuladoras
    const handleChangeReceituario = (value: CulturaPragaReceituario, id: number) => {
        setInternForm((prevState) => {
            if (prevState) {
                const index = prevState.findIndex(i => i.id === id)
                if (index >= 0) {
                    prevState[index] = { ...value, id }
                } else {
                    prevState.push({ ...value, id })
                }
                return [...prevState]
            } else {
                return [{ ...value, id }]
            }
        })
    }

    const handleGerar = () => {
        const params = {
            pedido_itens: internForm?.map(i => {
                return {
                    pedido_item_id: i.id,
                    cultura_id: i.cultura_id,
                    praga_id: i.praga_id
                }
            }) ?? [],
            agronomo_id: agronomo?.id
        }
        gerarReceituarios(params)
    }



    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Gerar receituário agronômico</DialogTitle>
            <DialogContent>
                {
                    alerta.length > 0 &&
                    <Alert
                        severity="warning"
                        sx={{ border: "1px solid #FE9B10", borderRadius: 5, mb: 2 }}
                    >
                        {
                            alerta.map((item, index) => {
                                return (
                                    <Box key={index} ml={2} color={"#D67D00"}>
                                        {item.alerta}
                                    </Box>
                                )
                            })
                        }
                    </Alert>
                }
                <Box my={2}>
                    <SelectAgronomo
                        required
                        value={agronomo}
                        onChange={(value) => setAgronomo(value)}
                    />
                    {
                        agronomo &&
                        <Stack direction={'row'} gap={6} alignItems={'center'} my={2}>
                            <DadoValorPrinter
                                alignValue='right'
                            titulo="Receitas emitidas"
                            valor={`${agronomo.receitas_emitidas}`}
                            />
                             <DadoValorPrinter
                                 alignValue='right'
                                 titulo="Receitas restantes"
                            valor={`${agronomo.quantidade_receitas_art - agronomo.receitas_emitidas}`}
                            />
                        </Stack>
                    }
                </Box>
                <TableContainer sx={{ mb: 2 }} >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCellSemBorder><b>Produto</b></TableCellSemBorder>
                                <TableCellSemBorder><b>Cultura/Praga</b></TableCellSemBorder>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                itemReceituario.map((item, index) => {
                                    return (
                                        <TableRow key={index}>
                                            <TableCellSemBorder>{item.nome}</TableCellSemBorder>
                                            <TableCellSemBorder width={'70%'}>
                                                <SelectCulturaReceituario
                                                    inputLabel="Cultura/Praga"
                                                    value={internForm?.find(i => i.id === item.id) as CulturaPragaReceituario}
                                                    onChangeValue={(e) => { handleChangeReceituario(e, item.id) }}
                                                    agrotoxico_id={item.agrotoxico_id ?? 0}
                                                    disabled={item.receita_id !== null || !item.agrotoxico_id}
                                                />
                                            </TableCellSemBorder>

                                        </TableRow>
                                    )
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancelar
                </Button>
                <Button
                    onClick={handleGerar}
                    color="primary"
                    variant="contained"
                    disabled={!permiteGerar || isLoading || !culturasPreenchidas}
                >
                    {isLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    Gerar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogGerarReceituarioProps {
    open: boolean;
    handleClose: () => void;
    data: IDataDetalharPedido
}

interface InternForm extends CulturaPragaReceituario { id: number }